import { FC, useState } from 'react';

import { ImagesApp } from '../../../constants/images';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import { useModalContext } from '../../../hooks/contexts/modal.context';
import formatNumericValue from '../../../utils/formatNumericValue';
import FeesExplanation from './FeesExplanation';
import * as Styles from './summary.styles';

type FeesBreakdownProps = {
  serviceFee: number;
  currencyFormat: string;
};

/**
 * Discloses the fees on top of the tickets before the buyer leaves the cart
 * (Decreto 13.108/2026, art. 7º). Only the service fee is known here: the
 * processing fee and installment interest depend on the payment method, so the
 * gateway prices them on the payment step and this block only announces them.
 */
const FeesBreakdown: FC<FeesBreakdownProps> = ({ serviceFee, currencyFormat }) => {
  const { translate } = useI18nContext();
  const { defineTitleModal, setContentModal, toggleModal } = useModalContext();
  // Collapsed by default (product decision, MKT-1043): the fee total stays on screen and the
  // breakdown is one click away, as in the gateway checkout.
  const [showDetails, setShowDetails] = useState(false);

  const formattedServiceFee = formatNumericValue({ value : serviceFee, discount : 0, currencyFormat });

  // The "?" opens the same explanation modal the gateway checkout shows, so both steps match.
  const openExplanation = () => {
    defineTitleModal(translate(['SUMMARY_FEES', 'EXPLANATION', 'TITLE']));
    setContentModal(<FeesExplanation />);
    toggleModal({ open : true });
  };

  return (
    <Styles.Fees id="summary-fees">
      <Styles.FeesRow>
        <Styles.FeesTitle id="summary-fees-title">
          {translate(['SUMMARY_FEES', 'TITLE'])}
          <Styles.FeesInfoTrigger
            type="button"
            id="summary-fees-info"
            aria-label={translate(['SUMMARY_FEES', 'INFO_LABEL'])}
            aria-haspopup="dialog"
            onClick={openExplanation}
          >
            <img src={ImagesApp.svg.icInfoPayment} alt="" />
          </Styles.FeesInfoTrigger>
        </Styles.FeesTitle>
        <Styles.FeesValue id="summary-fees-total">{formattedServiceFee}</Styles.FeesValue>
      </Styles.FeesRow>
      <Styles.FeesToggle
        type="button"
        id="summary-fees-toggle"
        aria-expanded={showDetails}
        aria-controls="summary-fees-details"
        onClick={() => setShowDetails((current) => !current)}
      >
        {translate(['SUMMARY_FEES', showDetails ? 'HIDE_DETAILS' : 'SHOW_DETAILS'])}
        <Styles.FeesChevron src={ImagesApp.svg.arrowUp} alt="" $collapsed={!showDetails} />
      </Styles.FeesToggle>
      {/* Kept mounted so `aria-controls` above always points at an element. */}
      <Styles.FeesDetails id="summary-fees-details" hidden={!showDetails}>
        <Styles.FeesRow>
          <Styles.FeesValue>{translate(['SUMMARY_FEES', 'SERVICE_FEE'])}</Styles.FeesValue>
          <Styles.FeesValue id="summary-fees-service-fee">{formattedServiceFee}</Styles.FeesValue>
        </Styles.FeesRow>
        <Styles.FeesNote id="summary-fees-payment-method-note">
          {translate(['SUMMARY_FEES', 'PAYMENT_METHOD_FEES'])}
        </Styles.FeesNote>
      </Styles.FeesDetails>
      <Styles.Divider />
    </Styles.Fees>
  );
};

export default FeesBreakdown;
